import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, Users, ChefHat, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/lib/useSeo";
import RichText from "@/components/RichText";

export default function RecipeDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/public/recipes/${slug}`).then((r) => setD(r.data)).catch(() => nav("/404", { replace: true }));
  }, [slug, nav]);
  useSeo(d ? { title: `${d.recipe.title} | Healthy Roots Recipes`, description: d.recipe.seo?.description || d.recipe.description, image: d.recipe.featured_image } : {});
  if (!d) return <div className="pt-40 text-center text-emerald-800">Loading…</div>;
  const r = d.recipe;
  return (
    <article className="pt-28 pb-24" data-testid="recipe-detail">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/recipes" className="inline-flex items-center gap-2 text-sm text-emerald-700 mb-6 hover:gap-3 transition-all"><ArrowLeft size={16} /> All Recipes</Link>
        <div className="flex gap-2 mb-4">{(r.categories || []).map((c) => <span key={c} className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">{c}</span>)}</div>
        <h1 className="text-4xl sm:text-5xl text-emerald-950 mb-4">{r.title}</h1>
        <p className="text-lg text-stone-600 mb-6">{r.description}</p>
        {r.review && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm mb-6">⚑ This recipe contains sample content for owner review. Original recipe text was not recoverable from the old site.</div>}
        <img src={r.featured_image} alt={r.title} className="w-full rounded-3xl shadow-md mb-8 max-h-[460px] object-cover" />
        <div className="grid grid-cols-3 gap-4 mb-10">
          <Stat icon={Clock} label="Prep" value={r.prep_time} />
          <Stat icon={ChefHat} label="Cook" value={r.cook_time} />
          <Stat icon={Users} label="Serves" value={r.servings} />
        </div>
        <div className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl text-emerald-900 mb-4">Ingredients</h2>
            <ul className="space-y-3">{(r.ingredients || []).map((it, i) => <li key={i} className="flex gap-3 items-start"><input type="checkbox" className="mt-1.5 accent-emerald-700" /><span className="text-stone-700">{it}</span></li>)}</ul>
          </div>
          <div className="md:col-span-3">
            <h2 className="text-2xl text-emerald-900 mb-4">Instructions</h2>
            <ol className="space-y-5">{(r.instructions || []).map((st, i) => <li key={i} className="flex gap-4"><span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-800 text-amber-50 flex items-center justify-center font-semibold">{i + 1}</span><p className="text-stone-700 pt-1">{st}</p></li>)}</ol>
            {r.content && <div className="mt-8"><RichText html={r.content} /></div>}
          </div>
        </div>
        {d.related?.length > 0 && (
          <div className="mt-16"><h2 className="text-2xl text-emerald-900 mb-6">More Recipes</h2><div className="grid sm:grid-cols-3 gap-5">{d.related.map((x) => <Link key={x.id} to={`/recipes/${x.slug}`} className="bg-white rounded-2xl overflow-hidden border border-stone-200/80 hover:shadow-md transition-all"><img src={x.featured_image} alt={x.title} className="h-32 w-full object-cover" /><p className="p-4 font-serif text-emerald-900">{x.title}</p></Link>)}</div></div>
        )}
      </div>
    </article>
  );
}

function Stat({ icon: Icon, label, value }) {
  return <div className="bg-white rounded-2xl p-5 border border-stone-200/80 text-center"><Icon className="mx-auto text-emerald-700 mb-2" size={20} /><p className="text-xs uppercase text-stone-500 tracking-wide">{label}</p><p className="font-mono font-semibold text-emerald-900">{value || "—"}</p></div>;
}
